import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import {
  getStoredConsent,
  storeConsent,
  initGTM,
  updateGTMConsent,
  type CookieConsent,
} from "@/lib/gtm";

const CookieBanner = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<CookieConsent>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Suppress banner when running inside the embeddable widget iframe
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("embed") === "widget" || params.get("embed") === "true") {
        return;
      }
    }
    const stored = getStoredConsent();
    if (stored) {
      // Consent already given — init GTM with stored preferences
      initGTM();
      updateGTMConsent(stored);
    } else {
      // No consent yet — show banner
      setVisible(true);
      initGTM(); // Load GTM in denied-by-default mode
    }
  }, []);

  const handleAcceptAll = () => {
    const full: CookieConsent = { necessary: true, analytics: true, marketing: true };
    storeConsent(full);
    updateGTMConsent(full);
    setVisible(false);
  };

  const handleDecline = () => {
    const minimal: CookieConsent = { necessary: true, analytics: false, marketing: false };
    storeConsent(minimal);
    updateGTMConsent(minimal);
    setVisible(false);
  };

  const handleSavePreferences = () => {
    storeConsent(consent);
    updateGTMConsent(consent);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] border-t border-border bg-card shadow-lg animate-in slide-in-from-bottom-4 duration-300">
      <div className="container mx-auto px-4 py-4">
        {!showDetails ? (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground max-w-2xl">
              {t("cookie.message")}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => setShowDetails(true)}>
                {t("cookie.manage")}
              </Button>
              <Button variant="outline" size="sm" onClick={handleDecline}>
                {t("cookie.decline")}
              </Button>
              <Button size="sm" onClick={handleAcceptAll}>
                {t("cookie.accept_all")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("cookie.manage_desc")}
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              {/* Necessary */}
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{t("cookie.necessary")}</p>
                  <p className="text-xs text-muted-foreground">{t("cookie.necessary_desc")}</p>
                </div>
                <Switch checked disabled className="opacity-70" />
              </div>
              {/* Analytics */}
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{t("cookie.analytics")}</p>
                  <p className="text-xs text-muted-foreground">{t("cookie.analytics_desc")}</p>
                </div>
                <Switch
                  checked={consent.analytics}
                  onCheckedChange={(v) => setConsent((c) => ({ ...c, analytics: v }))}
                />
              </div>
              {/* Marketing */}
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{t("cookie.marketing")}</p>
                  <p className="text-xs text-muted-foreground">{t("cookie.marketing_desc")}</p>
                </div>
                <Switch
                  checked={consent.marketing}
                  onCheckedChange={(v) => setConsent((c) => ({ ...c, marketing: v }))}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowDetails(false)}>
                {t("cookie.back")}
              </Button>
              <Button size="sm" onClick={handleSavePreferences}>
                {t("cookie.save")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
